import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { stringSorter } from "../../utils/functions.js";
import DataTable from "../../components/CustomDatatable/index.js";
import {
  Button,
  Card,
  Space,
  Typography,
  Breadcrumb,
  Row,
  Col,
  Popconfirm,
  Image,
} from "antd";
import { EditOutlined, HomeOutlined, PlusOutlined } from "@ant-design/icons";
import { Spinner } from "reactstrap";
import ModelsForm from "./BannerForm.js";
import { enhanceColumnsWithFilters } from "../../components/ColumnFilters/index.js";
import {
  addBanner,
  deleteBanner,
  getBanner,
  updateBanner,
  updateBannerStatus,
} from "../../store/actions/banner.js";
import CustomToggleSwitch from "../../components/CustomToggleSwitch.js";
import moment from "moment-timezone";

const { Title } = Typography;

const Banner = () => {
  document.title = "Banner";
  const [isLoading, setIsLoading] = useState(true);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);

  const dispatch = useDispatch();
  const banner = useSelector((state) => state.banner.list);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        await dispatch(getBanner());
        setIsLoading(false);
      } catch (error) {
        setIsLoading(false);
      }
    };

    fetchProducts();
  }, [dispatch]);

  const handleCreate = async (values) => {
    const formData = new FormData();

    // // Append other form fields
    formData.append("name", values.name);
    formData.append("url", values.url);
    formData.append("reference", values.reference);
    // Handle image upload
  if (values.image && values.image.length > 0) {
    // Assuming values.image is an array and we want to upload the first image
    const imageFile = values.image[0].originFileObj; // Ensure `originFileObj` is the File object

    if (imageFile) {
      formData.append("image", imageFile); // Append the file to FormData
    } else {
      console.error("Image file is missing");
    }
  } else {
    console.error("No image provided");
  }

    if (selectedRecord) {
      formData.append("id", selectedRecord._id);
      await dispatch(updateBanner({ ...selectedRecord, ...values }));
      window.location.reload();
      await dispatch(getBanner());
    } else {
      await dispatch(addBanner(formData));
      await dispatch(getBanner());
    }
    setIsModalVisible(false);
  };

  const handleDelete = async (id) => {
    await dispatch(deleteBanner(id));
    await dispatch(getBanner());
  };

  
  const handleChangeStatus = async (id, status) => {
    const formData = new FormData();
  
    formData.append('_id', id);
    formData.append('status', status);
    
    await dispatch(updateBannerStatus(id, status));
    await dispatch(getBanner());
  };

  const renderCell = (text) => (text ? text : 'N/A');
  let columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      sorter: stringSorter("name"),
      sortDirections: ["ascend", "descend"],
    },
    {
      title: "Image",
      dataIndex: "image", // Adjusted to match the correct data key
      key: "image",
      render: (image) =>
        image ? (
          <Image
            src={image}
            alt="Brand Image"
            style={{ width: "100px", height: "auto", cursor: "pointer" }}
          />
        ) : (
          <span>No Image</span>
        ),
    },
    {
      title: "Reference",
      dataIndex: "reference",
      key: "reference",
      sorter: stringSorter("reference"),
      sortDirections: ["ascend", "descend"],
    },
    {
      title: "Link",
      dataIndex: "url",
      key: "url",
      sorter: stringSorter("url"),
      sortDirections: ["ascend", "descend"],
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date) => moment(date).isValid() ? moment(date).format("YYYY-MM-DD HH:mm:ss") : "",
      sorter: (a, b) => moment(a.createdAt).isValid() && moment(b.createdAt).isValid() 
        ? moment(a.createdAt).unix() - moment(b.createdAt).unix() 
        : 0,
      sortDirections: ["ascend", "descend"],
    },
    {
      title: "Created By",
      dataIndex: "createdByName",
      key: "createdByName",
      sorter: stringSorter("createdByName"),
      sortDirections: ["ascend", "descend"],
      render: renderCell,
    },
    {
      title: "Updated At",
      dataIndex: "updatedAt",
      key: "updatedAt",
      render: (date) => moment(date).isValid() ? moment(date).format("YYYY-MM-DD HH:mm:ss") : "",
      sorter: (a, b) => moment(a.updatedAt).isValid() && moment(b.updatedAt).isValid() 
        ? moment(a.updatedAt).unix() - moment(b.updatedAt).unix() 
        : 0,
      sortDirections: ["ascend", "descend"],
    },
    {
      title: "Updated By",
      dataIndex: "updatedByName",
      key: "updatedByName",
      sorter: stringSorter("updatedByName"),
      sortDirections: ["ascend", "descend"],
      render: renderCell,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status, record) => (
        <CustomToggleSwitch
          record={record}
          onChangeStatus={handleChangeStatus}
        />
      ),
      sorter: (a, b) => a.status.localeCompare(b.status),
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <Space size="middle">
          <Button
            type="link"
            onClick={() => {
              setSelectedRecord(record);
              setIsModalVisible(true);
            }}
          >
            <EditOutlined />
          </Button>
          {/* <Popconfirm
            title="Are you sure you want to delete this record?"
            onConfirm={() => handleDelete(record._id)}
          >
            <Button type="link" danger>
              Delete
            </Button>
          </Popconfirm> */}
        </Space>
      ),
    },
  ];
  columns = enhanceColumnsWithFilters(columns);

  return (
    <div className="page-content">
      <Breadcrumb style={{ marginBottom: "16px" }}>
        <Breadcrumb.Item href="/">
          <HomeOutlined />
        </Breadcrumb.Item>
       <Breadcrumb.Item href="/dashboard">Dashboard</Breadcrumb.Item>
        <Breadcrumb.Item>Banner</Breadcrumb.Item>
      </Breadcrumb>

      <Card>
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          <Row justify="space-between" align="middle">
            <Col>
              <Title level={2} style={{ margin: 0 }}>
                Manage Banner
              </Title>
            </Col>
            <Col>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => {
                  setSelectedRecord(null);
                  setIsModalVisible(true);
                }}
              >
                Add Banner
              </Button>
            </Col>
          </Row>
          {isLoading ? (
            <div className="d-flex justify-content-center">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : (
            <DataTable
              dataSource={banner.map((item) => ({
                ...item,
                key: item._id,
              }))}
              columns={columns}
            />
          )}
        </Space>
      </Card>
      <ModelsForm
        visible={isModalVisible}
        onCreate={handleCreate}
        onCancel={() => setIsModalVisible(false)}
        record={selectedRecord}
      />
    </div>
  );
};

export default Banner;
